import React from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, GradientButton, CTABanner } from '../../components/common/Primitives';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import { SP_FAQ } from '../../lib/mockStudent';
import { Check } from 'lucide-react';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';

export default function StudentFAQ() {
  const d = SP_FAQ;
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
          <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy max-w-3xl">{d.hero.title}</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
          <div className="mt-6"><LicenceFeeHighlight text="No software licence fee through June 2027" /></div>
          <div className="mt-6 flex flex-wrap gap-3">
            <GradientButton to="/access-request">Request Access</GradientButton>
            <GradientButton to="#faqs" variant="outline">View FAQs</GradientButton>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1.5 text-[11.5px] font-bold tracking-widest text-purple">{d.hero.pill}</div>
        </div>
      </section>

      {/* CORE GUIDANCE */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.core.eyebrow} title={d.core.title} desc={d.core.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {d.core.items.map((c,i)=>(
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft text-center">
                <div className={`mx-auto grid h-14 w-14 place-items-center rounded-full font-bold ${['bg-purple-50 text-purple','bg-royal-50 text-royal','bg-cyan-50 text-cyan','bg-mint-50 text-mint'][i]}`}>{c.badge}</div>
                <div className="mt-3 text-[14px] font-bold text-navy">{c.title}</div>
                <p className="mt-1 text-[12.5px] text-ink-500">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SESSION CHECKLIST */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.checklist.eyebrow} title={d.checklist.title} desc={d.checklist.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.checklist.groups.map((g,i)=>(
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-bold text-white ${['bg-purple','bg-royal','bg-mint'][i]}`}>{g.n}</span>
                  <div className="text-[15px] font-bold text-navy">{g.title}</div>
                </div>
                <ul className="mt-4 space-y-2">{g.items.map((s,j)=>(<li key={j} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQS */}
      <section id="faqs" className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.faqs.eyebrow} title={d.faqs.title} desc={d.faqs.desc} />
          <div className="mt-8 mx-auto max-w-[900px]">
            <Accordion type="single" collapsible className="space-y-3">
              {d.faqs.items.map((f,i)=>(
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-canvas-200 bg-white shadow-soft data-[state=open]:shadow-card">
                  <AccordionTrigger className="px-5 py-4 text-left text-[15px] font-semibold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="px-5 pb-4 text-[13.5px] text-ink-500">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={{ label: d.cta.button, to: '/access-request' }} />
        </div>
      </section>
    </PageShell>
  );
}
